import { Injectable } from "@angular/core";
import { MediaRepository } from "../../../domain/media-repository";
import {
  MediaInfo,
  MediaItem,
  MediaLinkedType,
  UpsertSegmnetEntry,
  UpsertSegmnetsResultEntry,
} from "../../../domain/media";
import { HttpService } from "../../http/http";
import { MediaMapper } from "./media.mapper";
import { HttpParams } from "@angular/common/http";
import { Subject } from "rxjs";
import moment from "moment";
import { TenantRepository } from "../../../domain/tenant-repository";
import {
  MediaQCWorkflowObjective,
  MediaQCWorkflowObjectiveUpdateSpecifications,
} from "app/js/src/domain/qc-workflow";

@Injectable()
export class MediaRepositoryImpl implements MediaRepository {
  constructor(
    private httpService: HttpService,
    private mediaMapper: MediaMapper,
    private tenantRepository: TenantRepository,
  ) {}

  mediaUpdated = new Subject<MediaInfo>();
  u;
  private mediaAssociatedFilesSubject = new Subject<void>();
  public readonly mediaAssociatedFilesUpdated =
    this.mediaAssociatedFilesSubject.asObservable();

  getMedia(
    searchQuery: string,
    offset: number,
    orderByMedia: number,
    sortByMedia: string,
    startDateCreatedMedia: moment.Moment,
    endDateCreatedMedia: moment.Moment,
    startDateLicense: moment.Moment,
    endDateLicense: moment.Moment,
    statusChosen: string,
    countries: number[],
    tagsIds: number[],
    assetTypeIds: number[],
    languages: string[],
  ): Promise<MediaItem[]> {
    const tagsUrl = tagsIds.map((x) => `tags=${x}`).join("&");
    const countriesUrl = countries.map((x) => `countries=${x}`).join("&");
    const assetTypesUrl = assetTypeIds.map((x) => `assetTypes=${x}`).join("&");

    const url =
      `usermedia/getmedialist?SearchQuery=${searchQuery}` +
      `&Limit=20&Offset=${offset ?? 0}&OrderBy=${orderByMedia ?? 0}&SortBy=${sortByMedia}` +
      `&DateFrom=${
        startDateCreatedMedia ? startDateCreatedMedia.format("YYYYMMDD") : ""
      }` +
      `&DateTo=${
        endDateCreatedMedia ? endDateCreatedMedia.format("YYYYMMDD") : ""
      }` +
      `&LicenseFrom=${
        startDateLicense ? startDateLicense.format("YYYYMMDD") : ""
      }` +
      `&LicenseTo=${endDateLicense ? endDateLicense.format("YYYYMMDD") : ""}` +
      `&${tagsUrl}&${assetTypesUrl}&${countriesUrl}&Status=${statusChosen}&Languages=${languages}`;

    return this.httpService.get(url).then((response) => {
      return this.mediaMapper.mapGetAllMediaResponse(response);
    });
  }

  getMediaIdByTenantUniqueId(tenantUniqueId: string): Promise<number> {
    return new Promise<number>((resolve) => {
      this.httpService
        .get(`UserMedia/getId?customId=${tenantUniqueId}`)
        .then((mediaId) => resolve(mediaId))
        .catch((error) => {
          if (error.status === 404) {
            resolve(0);
          } else {
            resolve(-1);
          }
        });
    });
  }

  getMediaInfo(id: number): Promise<MediaInfo> {
    return this.httpService
      .get(`UserMedia/${id}`)
      .then(this.mediaMapper.mapGetMediaInfoResponse);
  }

  createContract(): Promise<number> {
    return this.httpService.post("UserMedia/CreateContract", {}).then((res) => {
      return res;
    });
  }

  update(mediaInfo: MediaInfo): Promise<void> {
    return this.httpService
      .post("UserMedia", this.mediaMapper.mapUpdateMediaInfoRequest(mediaInfo))
      .then(() => {
        this.mediaUpdated.next(mediaInfo);
        this.mediaAssociatedFilesSubject.next();
      });
  }

  deleteMedia(id: number): Promise<void> {
    return this.httpService.delete(`UserMedia/${id}`).then(() => {
      this.mediaAssociatedFilesSubject.next();
    });
  }

  contentUpload(
    url: string,
    filename: string,
    connectionId: string,
    parentMediaId?: number,
    mediaLinkedType?: MediaLinkedType,
    contractId?: number,
    customProps?: any,
  ) {
    return this.httpService
      .post("ContentUpload", {
        contentUrl: url,
        userFileName: filename,
        connectionId: connectionId,
        parentMediaId: parentMediaId,
        mediaLinkedType: mediaLinkedType,
        contractId: contractId,
        ...customProps,
      })
      .then((response: any) => {
        this.mediaAssociatedFilesSubject.next();
        return {
          mediaId: response.MediaId,
          mediaUid: response.MediaGuid,
        };
      });
  }

  packshotUpload(url: string, connectionId: string, mediaId: number) {
    return this.httpService
      .post("ContentUpload/UploadMediaPackshot", {
        contentUrl: url,
        connectionId: connectionId,
        mediaId: mediaId,
      })
      .then((val) => {
        this.mediaAssociatedFilesSubject.next();
        return val;
      });
  }

  async LogoUploadAnalyze(
    channelId: number,
    url: string,
    isVideo: boolean,
  ): Promise<any> {
    return await this.httpService.post("ContentUpload/AnalyzeLogo", {
      ChannelId: channelId,
      ContentUrl: url,
      IsVideo: isVideo,
    });
  }

  isTenantMediaIdUnique(tenantMediaId: string, ignoreMediaId?: number) {
    const urlParams = [`Id=${tenantMediaId}`];
    if (ignoreMediaId) {
      urlParams.push(`MediaId=${ignoreMediaId}`);
    }

    return this.httpService.get("UserMedia/CheckUniqueId", {
      params: new HttpParams({ fromString: urlParams.join("&") }),
    });
  }

  uploadSubtitle(file: File, mediaId) {
    const formData = new FormData();
    formData.append("file", file);
    const options = this.httpService.createDefaultRequestOptions();
    options.headers = options.headers.delete("Content-Type");
    options.params = new HttpParams({
      fromString: [`FileName=${file.name}`, `MediaId=${mediaId}`].join("&"),
    });
    return this.httpService
      .post("subtitle", formData, options)
      .then((result) => {
        this.mediaAssociatedFilesSubject.next();
        return {
          id: result.Id,
          language: result.Language,
          offset: result.Offset,
          fps: result.Fps,
          playbackSpeed: result.PlaybackSpeed,
        };
      });
  }

  uploadClosedCaption(objectKey: string, fileName: string, mediaId) {
    const options = this.httpService.createDefaultRequestOptions();
    options.params = new HttpParams({
      fromString: [
        `ObjectKey=${encodeURIComponent(objectKey)}`,
        `FileName=${encodeURIComponent(fileName)}`,
        `MediaId=${mediaId}`,
      ].join("&"),
    });
    return this.httpService
      .post("subtitle/PostClosedCaption", null, options)
      .then((result) => {
        this.mediaAssociatedFilesSubject.next();
        return {
          id: result.Id,
          language: result.Language,
          offset: result.Offset,
          fps: result.Fps,
          playbackSpeed: result.PlaybackSpeed,
          flavor: result.Flavor,
        };
      });
  }

  upsertSegments(
    segments: Array<UpsertSegmnetEntry>,
  ): Promise<Array<UpsertSegmnetsResultEntry>> {
    /*return new Promise<Array<UpsertSegmnetsResultEntry>>(resolve => {
        setTimeout(() => {
          resolve([new UpsertSegmnetsResultEntry(EUpsertSegmnetsResultStatus.Error, 'Test'),new UpsertSegmnetsResultEntry(EUpsertSegmnetsResultStatus.Error, 'Test')]);
        }, 2000);
      });*/
    const segmentRequest = segments.map((x) => {
      return {
        TenantMediaUniqueId: x.tenantMediaUniqueId,
        Description: x.description,
        TcIn: x.tcIn,
        TcOut: x.tcOut,
      };
    });
    return this.httpService
      .post(`UserMedia/BulkUpsertSegments`, {
        Segments: segmentRequest,
      })
      .then((result) => {
        return result.map(
          (x) =>
            new UpsertSegmnetsResultEntry(x.Status, x.Message, x.RequestIndex),
        );
      });
  }

  removeSubtitle(id: number) {
    return this.httpService.delete(`subtitle/${id}`).then((val) => {
      this.mediaAssociatedFilesSubject.next();
      return val;
    });
  }

  /** Backend GET UserMedia/{id}/PreviewUrl presigns with a fixed 15-minute TTL. */
  getMediaPreviewUrl(mediaId: number) {
    return this.httpService.get(`UserMedia/${mediaId}/previewUrl`);
  }

  getMediaPreviewFileUrl(mediaId: number) {
    return this.httpService.get(`UserMedia/${mediaId}/PreviewFile`);
  }

  getMediaTranscodedUrl(mediaId: number) {
    return this.httpService.get(`UserMedia/${mediaId}/TranscodedFile`);
  }

  downloadOriginalFile(mediaId: number) {
    return this.httpService.getMediaFile(
      `UserMedia/${mediaId}/OriginalFile`,
      `OriginalFile-${mediaId}`,
    );
  }

  downloadTranscodedFile(mediaId: number) {
    return this.httpService.getMediaFile(
      `UserMedia/${mediaId}/TranscodedFile`,
      `TranscodedFile-${mediaId}`,
    );
  }

  downloadPreview(mediaId: number) {
    return this.httpService.getMediaFile(
      `UserMedia/${mediaId}/PreviewFile`,
      `PreviewFile-${mediaId}`,
    );
  }

  getTenantMediasLanguages(): Promise<[{ code: string; name: string }]> {
    return this.tenantRepository.getTenantMediasLanguages();
  }
  updateMediaQCWorkflowObjectives(
    specifications: MediaQCWorkflowObjectiveUpdateSpecifications,
  ): Promise<void> {
    return this.httpService.post(
      `UserMedia/${specifications.mediaId}/UpdateMediaQCWorkflowObjectives`,
      { objectives: specifications.objectives },
    );
  }
  addQCWorkflowObjective(
    mediaId: number,
  ): Promise<Array<MediaQCWorkflowObjective>> {
    return this.httpService.get(`UserMedia/${mediaId}/AddQCWorkflowObjective`);
  }
}
